/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./DonationRecord.css";
import { simpleFetch } from "../../restWrapper";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { NumericFormat } from "react-number-format";
import { Helmet } from "react-helmet";
import { getOrganizationID, validatePlatformMatch, resolveThemeColors } from "../../assets/helper";
import { PLATFORM } from "../../config";

class DonationRecord extends Component {
  constructor() {
    super();
    const subdomain = getOrganizationID(window.location.href).subdomain;
    const domain = getOrganizationID(window.location.href).domain;
    const isCustomDomain = getOrganizationID(window.location.href).isCustomDomain;
    this.state = {
      fetching: false,
      subdomain: subdomain,
      domain: domain,
      isCustomDomain: isCustomDomain,
      buttonColor: "",
      headerColor: "",
      orgId: "",
      orgName: "",
      logo: "",
      paymentRecords: [],
      lastId: 0,
      hasReauthenticateTransactions: false,
      frequencyMenuOpen: false,
    };
    this.fetchData = this.fetchData.bind(this);
    this.checkReauthenticateTransactions = this.checkReauthenticateTransactions.bind(this);
    this.toggleFrequencyMenu = this.toggleFrequencyMenu.bind(this);
  }
  componentDidMount() {
    simpleFetch("/donors/organization", "GET", {
      subDomain: this.state.isCustomDomain ? this.state.domain : this.state.subdomain,
      isCustomDomain: this.state.isCustomDomain,
    }).then((res) => {
      // console.log('[DonationRecord] Organization response:', res.body);
      // console.log('[DonationRecord] Setting orgId to:', res.body.id);

      // Validate platform matches organization type
      const platformMatch = validatePlatformMatch(
        res.body.organizationObj.organizationType,
        PLATFORM
      );

      if (!platformMatch) {
        // console.error("Cannot display donation records: platform mismatch");
        this.props.history.push({ pathname: "/" + this.props.language });
        return;
      }

      this.setState({
        ...this.state,
        ...{
          buttonColor: res.body.organizationObj.buttonColor,
          headerColor: res.body.organizationObj.headerColor,
          progressBarColor: res.body.organizationObj.progressBarColor,
          orgId: res.body.id,
          orgName: res.body.organizationObj.organizationName,
          logo: res.body.organizationObj.logo,
        },
      }, () => {
        // Callback ensures orgId is set before fetching data (React 18 fix)
        // console.log('[DonationRecord] State after update, orgId:', this.state.orgId);
        this.fetchData();
        this.checkReauthenticateTransactions();
      });
    });
  }

  fetchData() {
    if (this.state.fetching) return false;
    if (!this.state.orgId) return false;
    this.setState({
      ...this.state,
      ...{
        fetching: true,
      },
    });

    let findParams = {
      organizationViewId: this.state.orgId,
      status: "complete",
    };

    if (this.state.lastId) findParams.lastId = this.state.lastId;

    simpleFetch("/donors/payments", "GET", findParams).then((res) => {
      this.setState({
        ...this.state,
        ...{
          fetching: false,
        },
      });

      if (res.body.length === 0) {
        this.setState({
          ...this.state,
          ...{
            hasMore: false,
          },
        });
        return false;
      }
      const lastItem = res.body[res.body.length - 1];
      try {
        this.setState({
          lastId: lastItem.id,
          hasMore: true,
          paymentRecords: this.state.paymentRecords.concat(res.body),
        });
      } catch (e) {
        this.props.logout();
      }
    });
  }

  checkReauthenticateTransactions() {
    if (!this.state.orgId) {
      // console.log('[DonationRecord] checkReauthenticateTransactions: No orgId yet');
      return false;
    }

    let findParams = {
      organizationViewId: this.state.orgId,
      status: "reauthenticate",
      lastId: 0,
    };

    // console.log('[DonationRecord] Checking for reauthenticate transactions with params:', findParams);

    simpleFetch("/donors/payments", "GET", findParams).then((res) => {
      // console.log('[DonationRecord] Reauthenticate check response:', res.status, res.body);

      // If there are any reauthenticate transactions, show the tab
      if (res.body && res.body.length > 0) {
        // console.log('[DonationRecord] Found', res.body.length, 'reauthenticate transactions, showing tab');
        this.setState({
          hasReauthenticateTransactions: true,
        });
      } else {
        // console.log('[DonationRecord] No reauthenticate transactions found');
      }
    }).catch((err) => {
      // console.error('[DonationRecord] Error checking reauthenticate transactions:', err);
    });
  }

  toggleFrequencyMenu() {
    this.setState((prev) => ({
      frequencyMenuOpen: !prev.frequencyMenuOpen,
    }));
  }

  renderRecord(func, data) {
    const languageObj = this.props.languageObj.renderRecord;

    const itemKey = "paymentID" + data.id;

    const paymentId = data.organizationReceiptId
      ? data.organizationReceiptId
      : this.props.id;

    const rDate = new Date(data.scheduledDate);

    const d = rDate.getDate();
    const m = rDate.getMonth() + 1;
    const y = rDate.getFullYear();
    const h = rDate.getHours();
    const min = (rDate.getMinutes() < 10 ? "0" : "") + rDate.getMinutes();

    const date = y + "/" + m + "/" + d + " " + h + ":" + min;

    function onClickRecord() {
      func.props.history.push({
        pathname: "/DonationInfoProcessed",
        state: {
          buttonColor: func.state.buttonColor,
          headerColor: func.state.headerColor,
          orgName: func.state.orgName,
          logo: func.state.logo,
          data: data,
          date: date,
        },
      });
    }

    const hasHeaderColor =
      typeof this.state.headerColor === "string" &&
      this.state.headerColor.length > 0;
    const resolved = hasHeaderColor
      ? resolveThemeColors(this.state.headerColor, this.state.buttonColor)
      : { headerColor: "", buttonColor: "" };
    const accentColor = hasHeaderColor
      ? `#${resolved.headerColor}`
      : "#606266";
    const accentColorSoft = hasHeaderColor
      ? `#${resolved.headerColor}1A`
      : "#6062661A";

    const buttonColor =
      resolved.buttonColor && resolved.buttonColor.length > 0
        ? `#${resolved.buttonColor}`
        : accentColor;

    const headerStyle = {
      "--donation-frame-accent": accentColor,
      "--donation-frame-accent-soft": accentColorSoft,
      "--donation-arrow-color": accentColor,
      "--donation-arrow-hover-color": buttonColor,
      "--donation-text-color": accentColor,
      "--donation-logo-color": accentColor,
    };

    const arrowLabel =
      languageObj.viewDetails ||
      languageObj.details ||
      languageObj.btn ||
      languageObj.view ||
      "Details";
    const amountLabel = languageObj.amount || "Amount";
    const receiptNumberLabel = languageObj.receiptNumber || "Receipt#";
    const completedLabel = languageObj.completed || "Completed";

    const logoStyle = {
      backgroundImage: "url(" + this.state.logo + ")",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      border: "none",
    };

    // Badge text should always read "Completed" (or localized equivalent)
    const statusLabel = languageObj.completed || "Completed";

    return (
      <div
        className="DonationRecord-frame"
        key={itemKey}
        onClick={onClickRecord}
        style={headerStyle}
      >
        <div className="DonationRecord-cardHeader">
          <div className="DonationRecord-statusBadge">
            {statusLabel}
          </div>
          <div className="DonationRecord-dateTime">{date}</div>
        </div>

        <div className="DonationRecord-cardContent">
          <div className="DonationRecord-logoWrapper">
            <div className="DonationRecord-logo" style={logoStyle}></div>
          </div>
          <div className="DonationRecord-transactionInfo">
            <div className="DonationRecord-title">
              {func.state.orgName} - {data.projectName}
            </div>
            {data.organizationReceiptId && (
              <div className="DonationRecord-receiptNumber">
                {receiptNumberLabel}: {data.organizationReceiptId}
              </div>
            )}
            <div className="DonationRecord-amountRow">
              <div className="DonationRecord-amount">
                {amountLabel}: HK
                <NumericFormat
                  decimalSeparator="."
                  displayType="text"
                  valueIsNumericString={true}
                  value={data.amount}
                  allowNegative={false}
                  thousandSeparator={true}
                  prefix={"$"}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
              </div>
              <div className="DonationRecord-arrowPill">
                <span>{arrowLabel}</span>
                <div className="DonationRecord-nextArrow" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const languageObj = this.props.languageObj;
    const completedLabel = languageObj.completed || "Completed";

    const scrollBarStyle = {
      overflow: "inherit",
      marginBottom: "100px",
    };

    const headerStyle = {
      background: "#" + this.state.headerColor,
    };

    return (
      <div className="DonationRecord-view" data-testid="donationrecord-view">
        <Helmet>
          <title>{this.state.orgName}</title>
        </Helmet>
        <div className="DonationRecord-bg" style={headerStyle} />
        <div className="DonationRecord-container">
          
          <div className="DonationRecord-header">
            
            <Link to="/">
              <div className="DonationRecord-backContainer">
                <div className="DonationRecord-back">
                  <div className="DonationRecord-backBtn"></div>
                  <div className="DonationRecord-backText">
                    {languageObj.backTxt}
                  </div>
                </div>
              </div>
            </Link>

            <div className={`DonationRecord-frequencyContainer ${this.state.frequencyMenuOpen ? "is-open" : ""}`}>
              <Link to="/" className="DonationRecord-frequencyBack">
                <div className="DonationRecord-frequencyBackIcn" />
                <span>{languageObj.backTxt}</span>
              </Link>
              <button
                type="button"
                className="DonationRecord-frequencyToggle"
                onClick={this.toggleFrequencyMenu}
                aria-expanded={this.state.frequencyMenuOpen}
                aria-label={languageObj.statusToggle || "Toggle status filter"}
              >
                <div className="DonationRecord-tickIcn" />
                <span>{languageObj.completed || "Completed"}</span>
                <span className="DonationRecord-frequencyToggleIcon" />
              </button>
              <div className="DonationRecord-frequencyCurrent">
                {completedLabel}
              </div>
              <div className="DonationRecord-frequency">
                <div className="DonationRecord-tickGroup">
                  <div className="DonationRecord-tickIcn" />
                  <div className="DonationRecord-tickTxt">
                    {languageObj.completed}
                  </div>
                </div>

                <Link
                  to="/DonationRecordRecurring"
                  className="DonationRecord-scheduleGroup"
                >
                  <div className="DonationRecord-scheduleIcn" />
                  <div className="DonationRecord-scheduleTxt">
                    {languageObj.scheduled}
                  </div>
                </Link>

                <Link
                  to="/DonationRecordReauthenticate"
                  className="DonationRecord-reauthGroup"
                >
                  <div className="DonationRecord-reauthIcn" />
                  <div className="DonationRecord-reauthTxt">
                    {languageObj.reauthenticate}
                  </div>
                </Link>
              </div>
            </div>

            <div className="DonationRecord-btn">{languageObj.btn}</div>
          </div>

          <div className="DonationRecord-main">
            <InfiniteScroll
              dataLength={this.state.paymentRecords.length}
              next={this.fetchData}
              hasMore={this.state.hasMore}
              style={scrollBarStyle}
            >
              {this.state.paymentRecords.map((record) =>
                this.renderRecord(this, record)
              )}
            </InfiniteScroll>
          </div>
        </div>
      </div>
    );
  }
}

DonationRecord.propTypes = {
  language: PropTypes.string,
  languageObj: PropTypes.object,
  logout: PropTypes.func,
  id: PropTypes.string,
};

export default DonationRecord;
